import { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ChatMessage } from '@/types';
import { cn } from '@/lib/utils';
import { FileIcon, Download, Loader2 } from 'lucide-react';
import { getApiUrl } from '@/config/runtime-env';

interface MessageBubbleProps {
  message: ChatMessage;
  isOwnMessage: boolean;
  senderName: string;
  senderAvatar?: string;
}

export default function MessageBubble({
  message,
  isOwnMessage,
  senderName,
  senderAvatar
}: MessageBubbleProps) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);

  const formatTime = (date: Date | string) => {
    return new Date(date).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase();
  };

  // Load file URL if message has a file attachment
  useEffect(() => {
    if (message.messageType === 'file' && message.file?.id) {
      loadFileUrl(message.file.id);
    }
  }, [message.file?.id, message.messageType]);

  const loadFileUrl = async (fileId: string) => {
    setIsLoadingFile(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/storage/download-url/${fileId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFileUrl(data?.data?.downloadUrl);
      }
    } catch (error) {
      console.error('Failed to load file URL:', error);
    } finally {
      setIsLoadingFile(false);
    }
  };

  const renderFileAttachment = () => {
    if (message.messageType !== 'file' || !message.file) {
      return null;
    }

    const { fileName, fileType, fileSize } = message.file;
    const isImage = fileType?.startsWith('image/');

    return (
      <div className="mt-2">
        {isImage ? (
          // Image preview
          <div className="relative max-w-xs rounded-lg overflow-hidden">
            {isLoadingFile ? (
              <div className="w-full h-48 bg-gray-100 animate-pulse flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : fileUrl ? (
              <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={fileUrl}
                  alt={fileName}
                  className="max-w-full h-auto rounded-lg hover:opacity-90 transition-opacity cursor-pointer"
                  loading="lazy"
                />
              </a>
            ) : null}
          </div>
        ) : (
          // File download link
          <a
            href={fileUrl || '#'}
            download={fileName}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center space-x-2 p-3 rounded-lg border transition-colors",
              isOwnMessage
                ? "bg-blue-50 border-blue-200 hover:bg-blue-100"
                : "bg-gray-50 border-gray-200 hover:bg-gray-100",
              !fileUrl && "opacity-50 cursor-not-allowed"
            )}
            onClick={(e) => {
              if (!fileUrl) e.preventDefault();
            }}
          >
            <FileIcon className={cn(
              "w-5 h-5 flex-shrink-0",
              isOwnMessage ? "text-blue-600" : "text-gray-600"
            )} />
            <div className="flex-1 min-w-0">
              <p className={cn(
                "text-sm font-medium truncate",
                isOwnMessage ? "text-blue-900" : "text-gray-900"
              )}>
                {fileName}
              </p>
              <p className={cn(
                "text-xs",
                isOwnMessage ? "text-blue-600" : "text-gray-500"
              )}>
                {fileSize ? (fileSize / 1024).toFixed(1) : '0'} KB
              </p>
            </div>
            {isLoadingFile ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400 flex-shrink-0" />
            ) : (
              <Download className={cn(
                "w-4 h-4 flex-shrink-0",
                isOwnMessage ? "text-blue-600" : "text-gray-600"
              )} />
            )}
          </a>
        )}
      </div>
    );
  };

  const messageText = message.message || message.content;
  const messageTime = message.sentAt || message.createdAt;

  if (isOwnMessage) {
    return (
      <div className="flex items-start space-x-2 justify-end">
        <div className="chat-bubble bg-[#0891B2] text-white p-3 rounded-lg max-w-[70%]">
          {messageText && <p className="text-sm">{messageText}</p>}
          {renderFileAttachment()}
          <span className="text-xs text-blue-200 mt-1 block">
            {messageTime && formatTime(messageTime)}
          </span>
        </div>
        <div className="w-8 h-8 bg-gradient-to-br from-[#8B5CF6] to-[#0891B2] rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xs font-medium">
            {getInitials(senderName)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start space-x-2">
      <Avatar className="w-8 h-8 flex-shrink-0">
        {senderAvatar ? (
          <AvatarImage src={senderAvatar} alt={senderName} />
        ) : null}
        <AvatarFallback className="bg-gray-200 text-gray-600 text-xs">
          {getInitials(senderName)}
        </AvatarFallback>
      </Avatar>
      <div className="chat-bubble bg-gray-100 p-3 rounded-lg max-w-[70%]">
        {messageText && <p className="text-sm text-gray-800">{messageText}</p>}
        {renderFileAttachment()}
        <span className="text-xs text-gray-500 mt-1 block">
          {messageTime && formatTime(messageTime)}
        </span>
      </div>
    </div>
  );
}
